/**
 * Shield arm UI (V2 superreject-shield) — the S-21 Shield card as a screen,
 * v3 "AFTER-DARK ARCADE": the armed state is an ARMED NEON TIMER RING — a
 * cyan arc that drains through the 60-minute window around a tabular clock,
 * snapping in mechanically (stamp + a soft screen flinch) when the shield
 * arms. Unarmed is the honest bet pitch behind one big cyan button.
 *
 * "The gamble is the product": arming burns one credit and opens a 60-minute
 * window; a fizzle refunds nothing (EC-18) and the copy says so BEFORE the
 * tap (©shield.arm_explainer — honesty rule). Server-authoritative
 * everywhere: balance and window state come from /me/inventory, the arm from
 * POST /arsenal/shield/arm; the client renders, never computes (PRD §7.4).
 *
 * Also renders the SM-4 "Recent blocks" Shield log — the durable identity
 * payoff (attacker name + timestamp + one-tap block/report; the push is only
 * a courtesy duplicate). The log's block/report actions stay sober.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCopy, resolveCopy } from "@humble/shared";
import { Button, Card, shakeScreen, stampVariants, toast } from "../../design/index.js";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { useCountdown } from "../cooldown/use-countdown.js";
import "./shield.css";

interface ShieldState {
  state?: "unarmed" | "armed";
  armed_at?: string;
  expires_at?: string;
}

interface LogEntry {
  sr_id?: string;
  attacker?: { id?: string; display_name?: string | null };
  blocked_at?: string;
}

/** "mm:ss" / "hh:mm:ss" → seconds (display parse only — server time is truth). */
function secondsOf(t: string): number {
  const parts = t.split(":").map((n) => Number(n) || 0);
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

const RING_R = 84;
const RING_C = 2 * Math.PI * RING_R;

/** The armed neon timer ring — cyan arc draining through the window. */
function ArmedRing({ shield, remaining }: { shield: ShieldState; remaining: string }) {
  const total =
    shield.expires_at && shield.armed_at
      ? Math.max(
          1,
          (new Date(shield.expires_at).getTime() - new Date(shield.armed_at).getTime()) / 1000,
        )
      : 3600;
  const fraction = Math.max(0, Math.min(1, secondsOf(remaining) / total));
  const hudLine = formatCopy(resolveCopy("shield.armed_hud") ?? "SHIELD UP · {mm:ss}", {
    "mm:ss": remaining,
  });
  return (
    <motion.div
      className="shield-ring"
      variants={stampVariants}
      initial="initial"
      animate="enter"
    >
      <svg className="shield-ring__svg" viewBox="0 0 200 200" aria-hidden="true">
        <circle className="shield-ring__track" cx="100" cy="100" r={RING_R} />
        <circle
          className="shield-ring__arc"
          cx="100"
          cy="100"
          r={RING_R}
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - fraction)}
        />
      </svg>
      <div className="shield__hud shield-ring__center" role="timer" aria-label={hudLine}>
        <span className="shield-ring__stamp">{resolveCopy("shield.armed_stamp") ?? "SHIELD UP"}</span>
        <span className="shield-ring__time type-numeric">{remaining}</span>
      </div>
    </motion.div>
  );
}

export function ShieldScreen() {
  const [shield, setShield] = useState<ShieldState>({ state: "unarmed" });
  const [balance, setBalance] = useState<number | null>(null);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [acted, setActed] = useState<Record<string, "blocked" | "reported">>({});

  const refresh = useCallback(async () => {
    try {
      const [inv, me, logRes] = await Promise.all([
        api.get("/me/inventory"),
        api.get("/me"),
        api.get("/arsenal/shield/log"),
      ]);
      setShield((inv.shield as ShieldState | undefined) ?? { state: "unarmed" });
      setBalance((inv.balances as { shield?: number } | undefined)?.shield ?? 0);
      setCooldownActive(Boolean((me.cooldown as { active?: boolean } | undefined)?.active));
      setLog((logRes as LogEntry[] | undefined) ?? []);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, []);

  useEffect(() => {
    void refresh();
    ws.ensureConnected();
    const offs = [
      ws.on("shield.armed", () => void refresh()),
      ws.on("shield.fizzled", () => void refresh()),
      ws.on("shield.blocked", () => void refresh()),
    ];
    return () => offs.forEach((off) => off());
  }, [refresh]);

  const armed = shield.state === "armed";
  const remaining = useCountdown(armed ? (shield.expires_at ?? null) : null, { short: true });

  // the mechanical snap — the whole screen flinches once when the ring arms
  const wasArmed = useRef(armed);
  useEffect(() => {
    if (armed && !wasArmed.current) shakeScreen("soft");
    wasArmed.current = armed;
  }, [armed]);

  useEffect(() => {
    if (armed && remaining === "00:00") {
      const t = setTimeout(() => void refresh(), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [armed, remaining, refresh]);

  async function arm() {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/arsenal/shield/arm", { body: withEventId({}) });
      toast("Shield up. 60 minutes on the clock.", "success");
      await refresh();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  async function blockAttacker(entry: LogEntry) {
    const id = entry.attacker?.id;
    const key = entry.sr_id ?? "";
    if (!id || acted[key]) return;
    try {
      await api.post("/blocks", { body: { user_id: id } });
      setActed((a) => ({ ...a, [key]: "blocked" }));
      toast("Blocked. They are gone.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  async function reportAttacker(entry: LogEntry) {
    const key = entry.sr_id ?? "";
    if (!entry.sr_id || acted[key]) return;
    try {
      await api.post("/reports", { body: { category: "harassment", sr_id: entry.sr_id } });
      setActed((a) => ({ ...a, [key]: "reported" }));
      toast(resolveCopy("safety.report_ack") ?? "Report received.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  return (
    <div className="shield">
      <Card padding="lg" glow={armed ? "like" : "none"} className={`shield__card${armed ? " crt" : ""}`}>
        <div className="shield__head">
          <h1 className="type-display-3 shield__title">Shield</h1>
          <span className="badge shield__balance">
            {balance ?? "–"} in stock
          </span>
        </div>

        {armed ? (
          <div className="shield__armed">
            <ArmedRing shield={shield} remaining={remaining} />
            <p className="text-dim shield__armed-note">{resolveCopy("shield.armed_note")}</p>
          </div>
        ) : (
          <>
            <p className="text-dim shield__explainer">{resolveCopy("shield.arm_explainer")}</p>
            {cooldownActive && (
              <p className="shield__warning">{resolveCopy("shield.arm_during_cooldown")}</p>
            )}
            {(balance ?? 0) > 0 ? (
              <Button variant="like" size="lg" block loading={busy} onClick={() => void arm()}>
                {resolveCopy("shield.arm_cta") ?? "ARM — burn one, cover 60 min"}
              </Button>
            ) : (
              <>
                <p className="shield__warning">{resolveCopy("shield.no_credit")}</p>
                <Link to="/store" className="btn btn--primary btn--block">
                  {resolveCopy("store.shield_pitch")}
                </Link>
              </>
            )}
          </>
        )}
      </Card>

      {/* SM-4 shield log — the payoff stays sober (block/report are safety) */}
      <Card padding="md" className="shield__log">
        <h2 className="h-caps shield__log-title">Recent blocks</h2>
        {log.length === 0 ? (
          <p className="text-faint">
            Nothing reversed yet. Ideally it stays that way — but that&apos;s not why you bought a shield.
          </p>
        ) : (
          <ul className="shield__log-list">
            {log.map((entry) => (
              <li key={entry.sr_id} className="shield__log-row">
                <span className="shield__log-text">
                  {formatCopy(resolveCopy("shield.block_log_entry") ?? "{name} · {date}", {
                    name: entry.attacker?.display_name ?? "Someone",
                    date: entry.blocked_at ? new Date(entry.blocked_at).toLocaleString() : "",
                  })}
                </span>
                <span className="shield__log-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={Boolean(acted[entry.sr_id ?? ""])}
                    onClick={() => void reportAttacker(entry)}
                  >
                    {acted[entry.sr_id ?? ""] === "reported" ? "Reported" : "Report"}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={Boolean(acted[entry.sr_id ?? ""])}
                    onClick={() => void blockAttacker(entry)}
                  >
                    {acted[entry.sr_id ?? ""] === "blocked" ? "Blocked" : "Block"}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
